import { usePathname, useSearchParams } from 'next/navigation';
import { useState, useEffect } from 'react';

function CmsPaginator({ total, perPage }) {
  // Get current pathname
  const pathname = usePathname();

  // Get current query parameters
  const searchParams = useSearchParams();

  // Fonction pour extraire le chemin de base sans le numéro de page
  const getBasePath = () => {
    // Diviser le chemin en segments
    const segments = pathname.split('/');

    // Si le dernier segment est un nombre, le supprimer
    if (!isNaN(segments[segments.length - 1])) {
      segments.pop();
    }

    // Rejoindre les segments pour obtenir le chemin de base
    return segments.join('/');
  };

  // Déterminer la page actuelle à partir de l'URL
  const determineCurrentPage = () => {
    const segments = pathname.split('/');
    const lastSegment = segments[segments.length - 1];

    // Si le dernier segment est un nombre, c'est la page actuelle
    if (!isNaN(lastSegment)) {
      return Number(lastSegment);
    }

    // Sinon, c'est la page 1
    return 1;
  };

  // État pour stocker la page actuelle
  const [currentPage, setCurrentPage] = useState(determineCurrentPage());

  // Mettre à jour la page actuelle lorsque l'URL change
  useEffect(() => {
    setCurrentPage(determineCurrentPage());
  }, [pathname]);

  // Calculer le nombre total de pages en tenant compte des différentes tailles de page
  const calculateTotalPages = () => {
    if (total <= 8) return 1; // Si moins de 8 espaces, une seule page

    // Pour la première page, on affiche 8 espaces
    // Pour les pages suivantes, on affiche 20 espaces
    const remainingItems = total - 8;
    const additionalPages = Math.ceil(remainingItems / 22);

    return 1 + additionalPages;
  };

  const [data] = useState({
    totalPage: calculateTotalPages(),
  });

  if (data.totalPage <= 1) return null;

  // Fonction pour générer l'URL de la page
  const getPageUrl = (pageNumber) => {
    const basePath = getBasePath();

    // Pour la première page, on ne met pas de numéro
    if (pageNumber === 1) {
      return basePath;
    }
    // Pour les autres pages, on ajoute le numéro
    return `${basePath}/${pageNumber}`;
  };

  return (
    <nav aria-label="Pagination" className="flex items-center justify-center text-purple-5">
      <ul className="flex gap-x-2" role="list">
        {currentPage > 1 && (
          <li>
            <a
              className="flex items-center justify-center rounded-lg cursor-pointer size-10 bg-purple-5/10 hover:bg-purple-5/20"
              href={getPageUrl(currentPage - 1)}
              aria-label="Page précédente"
              rel="prev"
            >
              <svg width="20" height="10" viewBox="0 0 20 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.21967 0.46967C4.51256 0.176777 4.98744 0.176777 5.28033 0.46967C5.57322 0.762563 5.57322 1.23744 5.28033 1.53033L4.21967 0.46967ZM1.53033 5.28033C1.23744 5.57322 0.762563 5.57322 0.46967 5.28033C0.176777 4.98744 0.176777 4.51256 0.46967 4.21967L1.53033 5.28033ZM0.46967 5.28033C0.176777 4.98744 0.176777 4.51256 0.46967 4.21967C0.762563 3.92678 1.23744 3.92678 1.53033 4.21967L0.46967 5.28033ZM5.28033 7.96967C5.57322 8.26256 5.57322 8.73744 5.28033 9.03033C4.98744 9.32322 4.51256 9.32322 4.21967 9.03033L5.28033 7.96967ZM1 5.5C0.585786 5.5 0.25 5.16421 0.25 4.75C0.25 4.33579 0.585786 4 1 4V5.5ZM19 4C19.4142 4 19.75 4.33579 19.75 4.75C19.75 5.16421 19.4142 5.5 19 5.5V4ZM5.28033 1.53033L1.53033 5.28033L0.46967 4.21967L4.21967 0.46967L5.28033 1.53033ZM1.53033 4.21967L5.28033 7.96967L4.21967 9.03033L0.46967 5.28033L1.53033 4.21967ZM1 4H19V5.5H1V4Z" fill="black" />
              </svg>
            </a>
          </li>
        )}
        {[...Array(data.totalPage).keys()].map((pageIndex) => {
          const pageNumber = pageIndex + 1;
          const isCurrentPage = pageNumber === currentPage;
          const showEllipsis = data.totalPage > 9;
          
          // Conditions pour afficher les pages
          const isFirstPage = pageNumber === 1;
          const isLastPage = pageNumber === data.totalPage;
          const isAroundCurrent = Math.abs(pageNumber - currentPage) <= 2;
          const shouldShowPage = isFirstPage || isLastPage || isAroundCurrent;

          if (showEllipsis && !shouldShowPage) {
            // Afficher un ellipsis après la première page ou avant la dernière
            if (pageNumber === 2 && currentPage > 4) {
              return (
                <li key={`ellipsis-start`}>
                  <span className="flex items-center justify-center size-10">...</span>
                </li>
              );
            }
            if (pageNumber === data.totalPage - 1 && currentPage < data.totalPage - 3) {
              return (
                <li key={`ellipsis-end`}>
                  <span className="flex items-center justify-center size-10">...</span>
                </li>
              );
            }
            return null;
          }

          return (
            <li key={pageNumber}>
              <a
                className={`flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer size-10 ${
                  isCurrentPage ? 'bg-purple text-white' : 'bg-purple-5/10 hover:bg-purple-5/20'
                }`}
                href={getPageUrl(pageNumber)}
                aria-current={isCurrentPage ? 'page' : undefined}
                aria-label={`Page ${pageNumber}`}
              >
                {pageNumber}
              </a>
            </li>
          );
        })}
        {currentPage < data.totalPage && (
          <li>
            <a
              className="flex items-center justify-center rounded-lg cursor-pointer size-10 bg-purple-5/10 hover:bg-purple-5/20"
              href={getPageUrl(currentPage + 1)}
              aria-label="Page suivante"
              rel="next"
            >
              <svg width="20" height="10" viewBox="0 0 20 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.7803 0.46967C15.4874 0.176777 15.0126 0.176777 14.7197 0.46967C14.4268 0.762563 14.4268 1.23744 14.7197 1.53033L15.7803 0.46967ZM18.4697 5.28033C18.7626 5.57322 19.2374 5.57322 19.5303 5.28033C19.8232 4.98744 19.8232 4.51256 19.5303 4.21967L18.4697 5.28033ZM19.5303 5.28033C19.8232 4.98744 19.8232 4.51256 19.5303 4.21967C19.2374 3.92678 18.7626 3.92678 18.4697 4.21967L19.5303 5.28033ZM14.7197 7.96967C14.4268 8.26256 14.4268 8.73744 14.7197 9.03033C15.0126 9.32322 15.4874 9.32322 15.7803 9.03033L14.7197 7.96967ZM19 5.5C19.4142 5.5 19.75 5.16421 19.75 4.75C19.75 4.33579 19.4142 4 19 4V5.5ZM1 4C0.585786 4 0.25 4.33579 0.25 4.75C0.25 5.16421 0.585786 5.5 1 5.5V4ZM14.7197 1.53033L18.4697 5.28033L19.5303 4.21967L15.7803 0.46967L14.7197 1.53033ZM18.4697 4.21967L14.7197 7.96967L15.7803 9.03033L19.5303 5.28033L18.4697 4.21967ZM19 4H1V5.5H19V4Z" fill="black" />
              </svg>
            </a>
          </li>
        )}
      </ul>
    </nav>
  );
}

export default CmsPaginator;
